import { notification } from 'antd';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistorySettlementsTableStore } from 'store/historySettlementsTable';
import { useHistorySettlementsTableFiltersStore } from 'store/historySettlementsTableFilters';
import { useMerchantsStore } from 'store/merchants';
import Loader from 'ui/Loader';
import Table from 'ui/Table';
import { dateHelpers } from 'utils/helpers/dateHelpers';
import type { TComponent } from 'utils/types/component';

import { useOnce } from 'components/utils/hooks/useOnce';
import { usePageVisibility } from 'components/utils/hooks/usePageVisibility';

import { Filters } from './components/Filters';
import { CustomPagination } from './components/Paginations';
import { COLUMNS } from './columns';
import styles from './HistorySettlements.module.scss';

export const HistorySettlements: TComponent = () => {
    const { t } = useTranslation();

    const data = useHistorySettlementsTableStore('rows');
    const loading = useHistorySettlementsTableStore('isLoading');
    const fetchHistorySettlements = useHistorySettlementsTableStore('getTable');
    const exportSettlements =
        useHistorySettlementsTableStore('exportSettlements');
    const getMerchants = useMerchantsStore('getMerchants');

    const body = useHistorySettlementsTableFiltersStore('body');
    const params = useHistorySettlementsTableFiltersStore('params');

    const handleExport = () => {
        const startDate = body.startDate;
        const endDate = body.endDate;
        const merchantIdentities = body.merchantIdentities;

        if (startDate && endDate) {
            const isWithin14Days = dateHelpers.isWithin14Days(
                startDate,
                endDate,
            );

            if (!isWithin14Days) {
                return notification.error({
                    message: t(
                        'balance.historySettlementsExport.filterWrongDatePeriodError',
                    ),
                });
            }

            exportSettlements({
                startDate,
                endDate,
                merchantIdentities,
            });
        } else {
            notification.error({
                message: t(
                    'balance.historySettlementsExport.filterOutOfDateError',
                ),
            });
        }
    };

    useOnce(
        () => true,
        () => {
            fetchHistorySettlements(body, params);
            getMerchants();
        },
        [],
    );

    usePageVisibility(() => {
        fetchHistorySettlements(body, params);
    });

    return (
        <Loader active={loading}>
            <div className={styles.container}>
                <div className={cn(styles.actionBar, 'actionBar')}>
                    <Filters onExport={handleExport} />
                </div>
                <Table
                    columns={COLUMNS}
                    tableRows={data}
                    onChange={() => {}}
                    sortDirections={['ascend', 'descend']}
                    rowKey={(record) => record.identity}
                />
                <div className={styles.tableBottom}>
                    <CustomPagination />
                </div>
            </div>
        </Loader>
    );
};
